import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2, Mail, ShieldCheck } from 'lucide-react';

/**
 * VerifyEmail Component
 *
 * Handles email verification for customer users.
 * Extracts token from URL query params and calls the verification API.
 *
 * URL format: /verify-email?token=<verification_token>
 *
 * Flow:
 * 1. Extract token from URL
 * 2. Call POST /api/v1/auth/verify-email with { token }
 * 3. Show success/error state
 * 4. Redirect to login on success
 */

// API base URL - uses Envoy proxy which routes to Better Auth sidecar
// Note: VITE_API_BASE_URL includes /api/v1 suffix (e.g., '/api/v1' or 'http://localhost:8443/api/v1')
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

interface VerifyEmailState {
  status: 'loading' | 'success' | 'error' | 'no-token';
  message: string;
}

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerifyEmailState>({
    status: 'loading',
    message: 'Verifying your email...',
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setState({
        status: 'no-token',
        message: 'No verification token provided. Please check your email link.',
      });
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.detail || errorData.message || 'Email verification failed. The link may be expired or invalid.'
          );
        }

        setState({
          status: 'success',
          message: 'Your email has been verified successfully!',
        });

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: { emailVerified: true },
            replace: true,
          });
        }, 3000);
      } catch (err) {
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Email verification failed',
        });
      }
    };

    verifyEmail();
  }, [token, navigate]);

  const renderContent = () => {
    switch (state.status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 size={32} className="text-blue-600 animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Verifying Your Email</h2>
            <p className="text-zinc-500">{state.message}</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Email Verified!</h2>
            <p className="text-zinc-500 mb-6">{state.message}</p>
            <p className="text-sm text-zinc-400">Redirecting to login...</p>
            <Link
              to="/login"
              className="mt-4 inline-block text-blue-600 font-medium hover:underline"
            >
              Click here if not redirected
            </Link>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={32} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Verification Failed</h2>
            <p className="text-zinc-500 mb-6">{state.message}</p>
            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Go to Login
              </Link>
              <button
                onClick={() => window.location.reload()}
                className="block w-full py-3 px-4 bg-zinc-100 text-zinc-700 rounded-lg font-medium hover:bg-zinc-200 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        );

      case 'no-token':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail size={32} className="text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Missing Verification Token</h2>
            <p className="text-zinc-500 mb-6">{state.message}</p>
            <Link
              to="/login"
              className="block w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Go to Login
            </Link>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4 font-sans text-zinc-900 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-100/50 to-transparent -z-10"></div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
        <div className="p-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-blue-500/30">
                C
              </div>
              <span className="font-bold text-3xl text-zinc-800 tracking-tight">cfleet</span>
            </div>
          </div>

          {renderContent()}
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
          <ShieldCheck size={12} /> Secure Connection
        </div>
        <p className="text-xs text-zinc-400">© 2025 cfleet. All rights reserved.</p>
      </div>
    </div>
  );
};

export default VerifyEmail;
